/**
 * Custom virtual backgrounds uploaded by the user.
 *
 * Uploads are re-encoded before they are stored: a 12 MP phone photo becomes a
 * ≤1920×1080 JPEG, because the compositor draws this image on EVERY frame and a
 * 4000-px source costs real milliseconds per draw. Storage is localStorage, so
 * a background survives a reload without a server round trip — the image never
 * leaves the device, which is the point.
 */
export interface CustomBackground {
  readonly id: string;
  readonly label: string;
  readonly dataUrl: string;
}

const STORAGE_KEY = 'cyphex.teams.backgrounds.v1';
const MAX_ITEMS = 4;
const MAX_EDGE = 1920;
const JPEG_QUALITY = 0.82;
/** localStorage gives ~5 MB per origin and other features share it. */
const MAX_TOTAL_BYTES = 1_600_000;

export const CUSTOM_PREFIX = 'custom:';

export const isCustomBackground = (id: string): boolean => id.startsWith(CUSTOM_PREFIX);

function isBackground(value: unknown): value is CustomBackground {
  if (typeof value !== 'object' || value === null) return false;
  const id: unknown = Reflect.get(value, 'id');
  const label: unknown = Reflect.get(value, 'label');
  const dataUrl: unknown = Reflect.get(value, 'dataUrl');
  return typeof id === 'string' && typeof label === 'string' && typeof dataUrl === 'string';
}

export function loadCustomBackgrounds(): readonly CustomBackground[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isBackground) : [];
  } catch {
    return [];
  }
}

function persist(items: readonly CustomBackground[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* Quota exceeded — the background still works for this session. */
  }
}

/** Downscales and re-encodes. Rejects with a user-facing message on failure. */
async function normalise(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('This browser cannot process images.');
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/**
 * Adds an uploaded image and returns the new list, newest first.
 * Throws with a message the caller can show verbatim in a toast.
 */
export async function addCustomBackground(file: File): Promise<readonly CustomBackground[]> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  const dataUrl = await normalise(file);
  if (dataUrl.length > MAX_TOTAL_BYTES) throw new Error('That image is too large to save.');

  const entry: CustomBackground = {
    id: `${CUSTOM_PREFIX}${Date.now().toString(36)}`,
    label: file.name.replace(/\.[^.]+$/, '').slice(0, 40) || 'Custom',
    dataUrl,
  };

  const kept: CustomBackground[] = [entry];
  let bytes = dataUrl.length;
  for (const existing of loadCustomBackgrounds()) {
    if (kept.length >= MAX_ITEMS || bytes + existing.dataUrl.length > MAX_TOTAL_BYTES) break;
    bytes += existing.dataUrl.length;
    kept.push(existing);
  }
  persist(kept);
  return kept;
}

export function removeCustomBackground(id: string): readonly CustomBackground[] {
  const kept = loadCustomBackgrounds().filter((item) => item.id !== id);
  persist(kept);
  return kept;
}

export function findCustomBackground(id: string): CustomBackground | null {
  return loadCustomBackgrounds().find((item) => item.id === id) ?? null;
}
